// Shared safe-storage helper for the `noah-sync-<reportId>` localStorage
// namespace. NOAH sync progress is broadcast to every connected browser in
// a clinic (not just the one that started the sync), so a busy multi-clinic
// practice can accumulate dozens of entries an hour with no natural upper
// bound. Left unchecked that eventually exhausts the origin's ~5MB
// localStorage budget and every further write throws QuotaExceededError
// (ENG-1796). This module is the one place that touches these keys so the
// cap, eviction and defensive try/catch live in a single spot rather than
// being re-implemented (or missed) at each call site.
//
// ENG-1813 follow-up: with the cap below in force this namespace holds at most
// 50 entries of a few hundred bytes, so it can no longer be what exhausts the
// quota - and its eviction pass therefore cannot free enough to matter either.
// If you are reading this because of a QuotaExceededError here, the space is
// being held by some OTHER namespace and this is just the messenger: it is the
// only localStorage writer in the app that logs, while every other one
// (setupLocalStorageAPI, microphone-storage, usePersistedState, ...) swallows
// the failure in a bare `catch {}`. Measure before assuming:
//
//   Object.entries(localStorage)
//     .map(([k, v]) => [k, ((k.length + v.length) * 2 / 1024).toFixed(1) + "KB"])
//     .sort((a, b) => parseFloat(b[1]) - parseFloat(a[1])).slice(0, 20)
//
// The first culprit found this way was the LaunchDarkly bootstrap cache - see
// utils/launchdarkly-storage.util.ts.

export const NOAH_SYNC_KEY_PREFIX = "noah-sync-";

// Hard cap on how many noah-sync-* entries localStorage may hold at once.
const MAX_NOAH_SYNC_ENTRIES = 50;

// Entries idle/dismissed longer than this are stale and pruned on boot,
// mirroring the sync banners' own "clear after 10 minutes" behavior.
const STALE_ENTRY_MS = 600_000;

export interface NoahSubReportSyncState {
  status: "idle" | "syncing" | "completed";
  startedAt?: number;
  completedAt?: number;
}

export interface NoahSyncState {
  status: "idle" | "syncing" | "completed" | "failed";
  startedAt?: number;
  completedAt?: number;
  error?: string;
  subReports: Record<string, NoahSubReportSyncState>;
  totalSubReports?: number;
  dismissedAt?: number;
}

export const getNoahSyncStorageKey = (reportId: number) =>
  `${NOAH_SYNC_KEY_PREFIX}${reportId}`;

function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  return (
    error.name === "QuotaExceededError" ||
    error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    error.code === 22 ||
    error.code === 1014
  );
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`[noah-sync-storage] Failed to remove ${key}:`, error);
  }
}

function getNoahSyncKeys(): string[] {
  try {
    return Object.keys(localStorage).filter((key) =>
      key.startsWith(NOAH_SYNC_KEY_PREFIX)
    );
  } catch {
    return [];
  }
}

function getStateTimestamp(state: NoahSyncState): number {
  return state.completedAt ?? state.dismissedAt ?? state.startedAt ?? 0;
}

function getEntryTimestamp(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    return getStateTimestamp(JSON.parse(raw) as NoahSyncState);
  } catch {
    // Corrupt entries have no meaningful timestamp - evict them first.
    return 0;
  }
}

function evictOldestNoahSyncEntries(count: number): void {
  if (count <= 0) return;
  const oldestFirst = getNoahSyncKeys()
    .map((key) => ({ key, timestamp: getEntryTimestamp(key) }))
    .sort((a, b) => a.timestamp - b.timestamp);

  oldestFirst.slice(0, count).forEach(({ key }) => safeRemoveItem(key));
}

function enforceNoahSyncEntryCap(): void {
  const overage = getNoahSyncKeys().length - MAX_NOAH_SYNC_ENTRIES;
  if (overage > 0) {
    evictOldestNoahSyncEntries(overage);
  }
}

// Reported once per page, not once per write. NOAH sync progress arrives as a
// WebSocket event per sub-report per report, broadcast to every browser in the
// clinic, so the per-write log turned a full localStorage into ~1500 identical
// Datadog errors a day from 19 sessions (ENG-1813) - and none of them were
// actionable, because by then this namespace is capped at 50 small entries and
// has no meaningful space left to give back. What matters is that the browser
// is over quota, which the first report says as well as the four hundredth.
let quotaExhaustionReported = false;

function reportQuotaExhausted(key: string, error: unknown): void {
  if (quotaExhaustionReported) return;
  quotaExhaustionReported = true;
  console.error(
    `[noah-sync-storage] localStorage quota exceeded; falling back to in-memory state for ${key}. ` +
      `Further NOAH sync write failures this session will not be reported. ` +
      `This namespace is capped at ${MAX_NOAH_SYNC_ENTRIES} entries, so the quota is almost certainly held elsewhere.`,
    error
  );
}

/** Test seam - the once-per-page latch would otherwise leak between cases. */
export function resetQuotaExhaustionReportingForTests(): void {
  quotaExhaustionReported = false;
}

/**
 * Persists NOAH sync state to localStorage. Never throws: a write failure
 * (quota exceeded or otherwise) degrades to in-memory-only state for that
 * entry instead of crashing the app. Returns whether the write succeeded.
 */
export function writeNoahSyncState(
  reportId: number,
  state: NoahSyncState
): boolean {
  const key = getNoahSyncStorageKey(reportId);
  let serialized: string;
  try {
    serialized = JSON.stringify(state);
  } catch (error) {
    console.error(`[noah-sync-storage] Failed to serialize ${key}:`, error);
    return false;
  }

  try {
    localStorage.setItem(key, serialized);
    enforceNoahSyncEntryCap();
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      console.error(`[noah-sync-storage] Failed to persist ${key}:`, error);
      return false;
    }
    // Quota exceeded: evict a batch of the oldest entries and retry once.
    evictOldestNoahSyncEntries(
      Math.max(1, Math.ceil(getNoahSyncKeys().length / 4))
    );
    try {
      localStorage.setItem(key, serialized);
      enforceNoahSyncEntryCap();
      return true;
    } catch (retryError) {
      reportQuotaExhausted(key, retryError);
      return false;
    }
  }
}

/** Removes a single NOAH sync entry. Never throws. */
export function removeNoahSyncState(reportId: number): void {
  safeRemoveItem(getNoahSyncStorageKey(reportId));
}

/**
 * Reads every noah-sync-* entry, dropping stale (>10min) and corrupt
 * entries as it goes so an already over-quota browser self-heals the next
 * time it loads, without the user having to clear site data.
 */
export function readAndPruneNoahSyncEntries(): Record<number, NoahSyncState> {
  const now = Date.now();
  const result: Record<number, NoahSyncState> = {};

  getNoahSyncKeys().forEach((key) => {
    const reportId = Number(key.slice(NOAH_SYNC_KEY_PREFIX.length));
    const raw = localStorage.getItem(key);
    if (!Number.isFinite(reportId) || !raw) {
      safeRemoveItem(key);
      return;
    }

    let parsed: NoahSyncState;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      console.error(
        `[noah-sync-storage] Failed to parse NOAH sync state for ${key}:`,
        error
      );
      safeRemoveItem(key);
      return;
    }

    // Age off the entry's newest timestamp. A timestamp-less entry ages off
    // immediately rather than living forever: the sub-report handlers build
    // their fallback state as `{ status: "syncing", subReports: {} }` with no
    // `startedAt` (use-clinic-notifications.tsx), so a browser that joins a
    // sync already in progress writes exactly that shape - and a check that
    // only tests the fields it happens to have could never expire it.
    if (now - getStateTimestamp(parsed) > STALE_ENTRY_MS) {
      safeRemoveItem(key);
      return;
    }

    result[reportId] = parsed;
  });

  return result;
}
